import { formatClock } from "../utils/weekScheduleGrid";

/** Daily week-table / day-list slots. Times are HH:MM and editable in inputs. */
export const DAY_ACTIVITY_SLOTS = [
  { id: "standup", label: "Stand-Up", icon: "ti-microphone-2", start: "09:00", end: "09:15" },
  { id: "problem", label: "Problem Review", icon: "ti-target", start: "09:15", end: "09:45" },
  { id: "learning", label: "Learning", icon: "ti-book-2", start: "09:45", end: "11:00" },
  { id: "break", label: "Break", icon: "ti-cup", start: "11:00", end: "11:15" },
  { id: "sysdesign", label: "System Design", icon: "ti-topology-star", start: "11:15", end: "12:15" },
  { id: "lunch", label: "Lunch", icon: "ti-tools-kitchen-2", start: "12:15", end: "13:00" },
  { id: "coding", label: "Coding Practice", icon: "ti-code", start: "13:00", end: "14:00" },
  { id: "project", label: "Project Build", icon: "ti-hammer", start: "14:00", end: "15:30" },
  { id: "speak", label: "Speak & Solve", icon: "ti-messages", start: "15:30", end: "16:00" },
  { id: "placement", label: "Placement Prep", icon: "ti-briefcase", start: "16:00", end: "16:30" },
  { id: "agent", label: "AI Agent Builder", icon: "ti-robot", start: "16:30", end: "17:00" },
  { id: "game", label: "Refresh Game", icon: "ti-mood-smile", start: "17:00", end: "17:15" },
  { id: "profile", label: "Mini Build", icon: "ti-sparkles", start: "17:15", end: "17:45" },
  { id: "close", label: "Retrospective", icon: "ti-flag", start: "17:45", end: "18:00" },
];

/** Tonight's Homework task types. */
export const HOMEWORK_ACTIVITY_SLOTS = [
  { id: "leetcode", label: "LeetCode", icon: "ti-code" },
  { id: "exercism", label: "Exercism", icon: "ti-terminal-2" },
  { id: "linkedin", label: "LinkedIn", icon: "ti-brand-linkedin" },
  { id: "agent", label: "Agent Workbench", icon: "ti-robot" },
  { id: "case", label: "Case study", icon: "ti-briefcase" },
];

export const SLOT_OVERRIDE_KEY = "ap_slotOverrides";

export function emptySlotOverride() {
  return { mode: "keep", title: "", titleSet: false, notes: "", start: "", end: "" };
}

function isCustomId(id, catalog) {
  return !(catalog || []).some((s) => s.id === id);
}

function hhmm(raw) {
  const s = String(raw || "").trim();
  return /^\d{2}:\d{2}$/.test(s) ? s : "";
}

export function slotClock(row) {
  const a = hhmm(row?.start);
  const b = hhmm(row?.end);
  if (a && b) return `${a}-${b}`;
  return a || b || "";
}

function normalizeOne(id, row, catalog, withTimes, keepRaw = false) {
  const def = (catalog || []).find((s) => s.id === id);
  const custom = isCustomId(id, catalog);
  let mode = "keep";
  if (custom) mode = "add";
  else if (row?.mode === "replace" || row?.mode === "hide" || row?.mode === "add") mode = row.mode;
  const start = withTimes ? (hhmm(row?.start) || hhmm(def?.start) || "") : "";
  const end = withTimes ? (hhmm(row?.end) || hhmm(def?.end) || "") : "";
  return {
    mode,
    // keepRaw = editor mode: never trim/fallback while the user is typing,
    // otherwise a space or an emptied field snaps back and "one letter stays".
    title: keepRaw ? String(row?.title ?? "") : String(row?.title || "").trim(),
    notes: keepRaw ? String(row?.notes ?? "") : String(row?.notes || "").trim(),
    titleSet: keepRaw ? Boolean(row?.titleSet) : false,
    start,
    end,
    custom,
    sort: Number.isFinite(Number(row?.sort))
      ? Number(row.sort)
      : Math.max(0, (catalog || []).findIndex((s) => s.id === id)),
  };
}

export function extraCustomSlots(raw, catalog = DAY_ACTIVITY_SLOTS) {
  const known = new Set((catalog || []).map((s) => s.id));
  const extras = [];
  Object.entries(raw && typeof raw === "object" ? raw : {}).forEach(([id, row]) => {
    if (known.has(id)) return;
    if (row?.mode === "add" || String(id).startsWith("custom_")) {
      extras.push({
        id,
        label: String(row?.title || "").trim() || "New activity",
        icon: "ti-plus",
        custom: true,
      });
    }
  });
  return extras;
}

export function catalogWithCustom(raw, catalog = DAY_ACTIVITY_SLOTS) {
  return [...(catalog || []), ...extraCustomSlots(raw, catalog)];
}

export function normalizeSlotOverrides(raw, catalog = DAY_ACTIVITY_SLOTS, withTimes = catalog === DAY_ACTIVITY_SLOTS, opts = {}) {
  const keepRaw = Boolean(opts && opts.keepRaw);
  const src = raw && typeof raw === "object" ? raw : {};
  const out = {};
  (catalog || []).forEach((slot) => {
    out[slot.id] = normalizeOne(slot.id, src[slot.id], catalog, withTimes, keepRaw);
  });
  extraCustomSlots(src, catalog).forEach((slot) => {
    out[slot.id] = normalizeOne(slot.id, src[slot.id], catalog, withTimes, keepRaw);
  });
  return out;
}

export function splitStoredOverrides(raw, opts = {}) {
  if (raw && typeof raw === "object" && (raw.daily || raw.homework)) {
    return {
      daily: normalizeSlotOverrides(raw.daily, DAY_ACTIVITY_SLOTS, true, opts),
      homework: normalizeSlotOverrides(raw.homework, HOMEWORK_ACTIVITY_SLOTS, false, opts),
      timingKey: String(raw.timingKey || ""),
    };
  }
  return {
    daily: normalizeSlotOverrides(raw, DAY_ACTIVITY_SLOTS, true, opts),
    homework: normalizeSlotOverrides({}, HOMEWORK_ACTIVITY_SLOTS, false, opts),
    timingKey: "",
  };
}

function toMin(raw) {
  const s = hhmm(raw);
  if (!s) return null;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
}

function toHHMM(total) {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(Number(total) || 0)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function makeTimingKey({ dailyStart, dailyEnd, lunchStart, lunchEnd, breaks } = {}) {
  const br = (breaks || [])
    .filter((b) => hhmm(b?.start) && hhmm(b?.end))
    .map((b) => `${hhmm(b.start)}-${hhmm(b.end)}`)
    .join(",");
  return ["v2", hhmm(dailyStart), hhmm(dailyEnd), hhmm(lunchStart), hhmm(lunchEnd), br].join("|");
}

function readLsJson(key, fallback) {
  try {
    const val = localStorage.getItem(key);
    return val !== null ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

/** Basics lunch / break clocks — extra columns only, never overwrite activity Lunch/Break. */
export function readBasicsTiming(timing = {}) {
  const lunchStart = hhmm(timing.lunchStart) || hhmm(readLsJson("ap_lunchStart", ""));
  const lunchEnd = hhmm(timing.lunchEnd) || hhmm(readLsJson("ap_lunchEnd", ""));
  const fromArg = Array.isArray(timing.breaks) ? timing.breaks : null;
  const stored = fromArg || readLsJson("ap_breaks", []);
  const breaks = (Array.isArray(stored) ? stored : []).filter(
    (b) => toMin(b?.start) != null && toMin(b?.end) != null && toMin(b.end) > toMin(b.start)
  );
  return { lunchStart, lunchEnd, breaks };
}

export function basicsRestSlots(timing = {}) {
  const { lunchStart, lunchEnd, breaks } = readBasicsTiming(timing);
  const out = [];
  if (toMin(lunchStart) != null && toMin(lunchEnd) != null && toMin(lunchEnd) > toMin(lunchStart)) {
    out.push({
      id: "basics_lunch",
      title: "Lunch",
      notes: "",
      start: lunchStart,
      end: lunchEnd,
      clock: formatClock(`${lunchStart}-${lunchEnd}`),
      added: true,
    });
  }
  breaks.forEach((b, i) => {
    const start = hhmm(b.start);
    const end = hhmm(b.end);
    out.push({
      id: `basics_break_${i}`,
      title: breaks.length > 1 ? `Break ${i + 1}` : "Break",
      notes: "",
      start,
      end,
      clock: formatClock(`${start}-${end}`),
      added: true,
    });
  });
  return out;
}

const floor5 = (n) => Math.floor(n / 5) * 5;

function parseBreaks(breaks) {
  return (Array.isArray(breaks) ? breaks : [])
    .map((b) => ({ s: toMin(b?.start), e: toMin(b?.end) }))
    .filter((b) => b.s != null && b.e != null && b.e > b.s)
    .sort((a, b) => a.s - b.s);
}

/**
 * Lay the day out from Basics:
 *  - Day window  -> overall From / To
 *  - Lunch       -> the "Lunch" activity gets exactly the Basics lunch clock
 *  - Break(s)    -> the "Break" activity gets the first Basics break; extra
 *                   breaks become their own "Break N" rows
 *  - Everything else is spread across the free time around those fixed
 *    blocks, keeping the user's current order (so swaps survive) and the
 *    relative lengths. Deleted (hidden) rows stay deleted; names/content kept.
 */
export function syncDailySlotsToTiming(rawDaily, timing = {}) {
  const catalog = DAY_ACTIVITY_SLOTS;
  const existing = normalizeSlotOverrides(rawDaily, catalog, true, { keepRaw: true });
  const defStart = toMin(catalog[0].start) ?? 9 * 60;
  const defEnd = toMin(catalog[catalog.length - 1].end) ?? 18 * 60;
  let dayStart = toMin(timing.dailyStart);
  let dayEnd = toMin(timing.dailyEnd);
  if (dayStart == null) dayStart = defStart;
  if (dayEnd == null || dayEnd <= dayStart) dayEnd = Math.max(dayStart + 60, defEnd);

  // Basics rest blocks, clipped to the day window
  const clip = (b) => ({ s: Math.max(dayStart, b.s), e: Math.min(dayEnd, b.e) });
  const lunchRaw = { s: toMin(timing.lunchStart), e: toMin(timing.lunchEnd) };
  const lunch =
    lunchRaw.s != null && lunchRaw.e != null && lunchRaw.e > lunchRaw.s ? clip(lunchRaw) : null;
  const breaks = parseBreaks(timing.breaks).map(clip).filter((b) => b.e > b.s);
  const hasLunch = !!lunch && lunch.e > lunch.s;

  const next = { ...existing };
  const fixed = [];

  if (hasLunch) {
    fixed.push(lunch);
    const row = next.lunch || emptySlotOverride();
    next.lunch = { ...row, start: toHHMM(lunch.s), end: toHHMM(lunch.e) };
  }
  if (breaks.length) {
    fixed.push(breaks[0]);
    const row = next.break || emptySlotOverride();
    next.break = { ...row, start: toHHMM(breaks[0].s), end: toHHMM(breaks[0].e) };
  }
  // extra breaks -> own rows; drop stale auto rows
  Object.keys(next).forEach((id) => {
    if (/^basics_break_\d+$/.test(id)) delete next[id];
  });
  breaks.slice(1).forEach((b, i) => {
    fixed.push(b);
    const id = `basics_break_${i + 2}`;
    next[id] = {
      ...emptySlotOverride(),
      mode: "add",
      custom: true,
      title: `Break ${i + 2}`,
      titleSet: true,
      start: toHHMM(b.s),
      end: toHHMM(b.e),
      sort: 100 + i,
    };
  });

  // Free windows = day minus fixed blocks
  const merged = [];
  [...fixed]
    .sort((a, b) => a.s - b.s)
    .forEach((b) => {
      const last = merged[merged.length - 1];
      if (last && b.s <= last.e) last.e = Math.max(last.e, b.e);
      else merged.push({ ...b });
    });
  let segs = [];
  let cursor = dayStart;
  merged.forEach((b) => {
    if (b.s > cursor) segs.push({ s: cursor, e: b.s });
    cursor = Math.max(cursor, b.e);
  });
  if (cursor < dayEnd) segs.push({ s: cursor, e: dayEnd });
  segs = segs.filter((g) => g.e - g.s >= 5);
  if (!segs.length) segs = [{ s: dayStart, e: dayEnd }];

  // Activities that flow (everything except the rows pinned by Basics)
  const pinned = new Set(["basics_break_"]);
  const flow = catalogWithCustom(next, catalog)
    .filter((slot) => {
      const row = next[slot.id] || {};
      if (row.mode === "hide") return false;
      if (slot.id === "lunch" && hasLunch) return false;
      if (slot.id === "break" && breaks.length) return false;
      if ([...pinned].some((p) => String(slot.id).startsWith(p))) return false;
      return true;
    })
    .map((slot) => {
      const row = next[slot.id] || {};
      const def = catalog.find((c) => c.id === slot.id);
      const idx = Math.max(0, catalog.findIndex((c) => c.id === slot.id));
      const rs = toMin(row.start);
      const re = toMin(row.end);
      const ds = toMin(def?.start);
      const de = toMin(def?.end);
      let w = rs != null && re != null && re > rs ? re - rs : ds != null && de != null ? de - ds : 30;
      w = Math.max(10, w);
      return {
        id: slot.id,
        w,
        order: rs != null ? rs : ds != null ? ds : 9999 + idx,
        idx,
      };
    })
    .sort((a, b) => a.order - b.order || a.idx - b.idx);

  if (flow.length) {
    const totalW = flow.reduce((n, f) => n + f.w, 0);
    const totalFree = segs.reduce((n, g) => n + (g.e - g.s), 0);
    // split activities across segments in proportion to free time
    const buckets = segs.map(() => []);
    let si = 0;
    let acc = 0;
    flow.forEach((f) => {
      const share = (totalW * (segs[si].e - segs[si].s)) / totalFree;
      if (si < segs.length - 1 && buckets[si].length && acc + f.w / 2 > share) {
        si += 1;
        acc = 0;
      }
      buckets[si].push(f);
      acc += f.w;
    });
    buckets.forEach((items, i) => {
      if (!items.length) return;
      const g = segs[i];
      const len = g.e - g.s;
      const sumW = items.reduce((n, f) => n + f.w, 0);
      let t = g.s;
      items.forEach((f, k) => {
        const last = k === items.length - 1;
        const dur = last ? g.e - t : Math.max(5, floor5((len * f.w) / sumW));
        const end = last ? g.e : Math.min(g.e, t + dur);
        next[f.id] = { ...(next[f.id] || emptySlotOverride()), start: toHHMM(t), end: toHHMM(end) };
        t = end;
      });
    });
  }
  return next;
}

export function visibleDailyBlueprint(rawDaily) {
  const daily = normalizeSlotOverrides(rawDaily, DAY_ACTIVITY_SLOTS, true);
  const catalog = catalogWithCustom(daily, DAY_ACTIVITY_SLOTS);
  const slots = catalog
    .filter((s) => (daily[s.id] || {}).mode !== "hide")
    .map((s) => {
      const row = daily[s.id] || {};
      return {
        id: s.id,
        title: row.title || s.label,
        notes: row.notes || "",
        start: row.start || "",
        end: row.end || "",
        clock: formatClock(slotClock(row)),
        mode: row.mode || "keep",
      };
    });
  // Lunch / Break from Basics are real rows now (see syncDailySlotsToTiming), so no extra copies.
  return slots.sort((a, b) => (toMin(a.start) ?? 9999) - (toMin(b.start) ?? 9999));
}

export function iconForSlotFamily(family) {
  const id = String(family || "").split("#")[0];
  return DAY_ACTIVITY_SLOTS.find((s) => s.id === id)?.icon || "ti-layout-grid";
}

export function homeworkTaskFamily(kind, headline) {
  const t = `${kind || ""} ${headline || ""}`.toLowerCase();
  if (/leetcode/.test(t)) return "leetcode";
  if (/exercism/.test(t)) return "exercism";
  if (/linkedin/.test(t)) return "linkedin";
  if (/agent/.test(t)) return "agent";
  if (/case/.test(t)) return "case";
  return "";
}

export function activeSlotOverrides(overrides, catalog = DAY_ACTIVITY_SLOTS, withTimes = catalog === DAY_ACTIVITY_SLOTS) {
  const all = normalizeSlotOverrides(overrides, catalog, withTimes);
  const active = {};
  Object.entries(all).forEach(([id, row]) => {
    const def = (catalog || []).find((s) => s.id === id);
    if (row.mode === "hide") {
      active[id] = row;
      return;
    }
    if (row.mode === "add") {
      if (row.title || row.notes || row.start || row.end) active[id] = row;
      return;
    }
    const renamed = Boolean(row.title && (!def || row.title !== def.label));
    const retimed = Boolean(withTimes && def && (row.start !== def.start || row.end !== def.end));
    const resorted = Boolean(!withTimes && def && row.sort !== (catalog || []).findIndex((s) => s.id === id));
    if (row.mode === "replace" || renamed || row.notes || retimed || resorted || (row.mode === "keep" && (row.start || row.end))) {
      active[id] = row;
    }
  });
  return active;
}

function instructionLines(active, catalog, includeTime) {
  return Object.entries(active).map(([id, row]) => {
    const slot = (catalog || []).find((s) => s.id === id);
    const name = slot?.label || id;
    const shown = row.title || name;
    const clock = includeTime ? slotClock(row) : "";
    const timeBit = clock ? ` Time must be ${clock}.` : "";
    const notes = row.notes ? ` Content: ${row.notes}` : "";
    if (row.mode === "hide") return `- DELETE / omit "${name}". Do not put it in the table.`;
    if (row.mode === "add") return `- CREATE a new activity "${shown}".${timeBit}${notes}`;
    if (row.mode === "replace" || (row.title && row.title !== name) || row.notes) {
      return `- UPDATE "${name}" → "${shown}".${timeBit}${notes}`;
    }
    return clock ? `- UPDATE "${shown}" time to ${clock}.` : `- KEEP "${shown}".`;
  });
}

export function slotOverrideInstruction(bundle, timing = {}) {
  const split = splitStoredOverrides(bundle);
  const dailyCat = catalogWithCustom(split.daily, DAY_ACTIVITY_SLOTS);
  const hwCat = catalogWithCustom(split.homework, HOMEWORK_ACTIVITY_SLOTS);
  const daily = activeSlotOverrides(split.daily, DAY_ACTIVITY_SLOTS, true);
  const homework = activeSlotOverrides(split.homework, HOMEWORK_ACTIVITY_SLOTS, false);
  const blueprint = visibleDailyBlueprint(split.daily);
  const parts = [];
  const dayBits = [];
  if (hhmm(timing.dailyStart) && hhmm(timing.dailyEnd)) {
    dayBits.push(`Day window ${hhmm(timing.dailyStart)}–${hhmm(timing.dailyEnd)}`);
  }
  if (hhmm(timing.lunchStart) && hhmm(timing.lunchEnd)) {
    dayBits.push(`Lunch ${hhmm(timing.lunchStart)}–${hhmm(timing.lunchEnd)}`);
  }
  const br = (timing.breaks || []).filter((b) => hhmm(b?.start) && hhmm(b?.end));
  if (br.length) {
    dayBits.push(`Breaks ${br.map((b) => `${hhmm(b.start)}–${hhmm(b.end)}`).join(", ")}`);
  }
  if (timing.startDate && timing.endDate) {
    dayBits.push(`Dates ${timing.startDate} → ${timing.endDate}`);
  }
  if (timing.numDays) dayBits.push(`${timing.numDays} working days`);

  if (blueprint.length || Object.keys(daily).length) {
    parts.push(
      "DAY LAYOUT (copy these clocks exactly — LLM only writes Task/Content):",
      dayBits.length ? `Basics window: ${dayBits.join(" · ")}.` : "",
      "Use _dayTemplate / _activityBlueprint. Do not invent a different timetable.",
      "All clocks 12-hour IST (9:00–9:15 AM). Never UTC/GMT/24-hour.",
      "Only these periods, this order, these times. Do not add OKR or extra activities.",
      "Every period MUST have real Task/Content for that day. Never leave content blank. Never use placeholders like 'TBD' or 'today's concept'.",
      "Use the SAME activity title every day (from this list). Do not rename to Meditation, OKR, Presentation Topic, or Student Should Demonstrate unless that title is in the list.",
      "One cell = one activity. Do not pour Stand-Up + rest + KPIs + wireframe into one cell.",
      "PHASE 2: same content format as Phase 1. Project Build = full DT Playbook (Stage, Step, What to Learn, Real Project Task, Discussion Prompt).",
      "Every CREATE / add activity (including a manual AI Agent Builder) must get full Task/Content every day — same richness as Project Build.",
      "AI Agent Builder / Agent Workbench = SNS Agent Workbench tasks for THIS problem (build/test an n8n-style agent). Not OKRion. Not LinkedIn. Not certification.",
      "OKR / OKRion = LinkedIn article, certification, skill badge from the OKRion tool. Never mix into Agent Builder.",
      "Each technical cell: Problem (short) at the top. Deliverable at the end (what the student submits). Do not write Done when.",
      "Learning resources: one youtube.com/watch?v= video on THAT day's topic + one real article. No search-result URLs.",
      "Learning / Project: include Stage, Step, What to Learn, Real Project Task, Discussion Prompt, YouTube watch URL (youtube.com/watch?v=), one article URL, Done when.",
      "Never use geeksforgeeks.org/?s= search links. Use real watch/article URLs.",
      "Coding/Project/Placement/Agent: a concrete task tied to the problem or the student's banks — not a one-line title.",
      "Lunch/Break: short rest note only. Homework is after class, no clocks.",
      ...blueprint.map((row, i) => {
        const clock = formatClock(slotClock(row));
        const notes = row.notes ? ` Content: ${row.notes}` : "";
        return `${i + 1}. ${row.title}${clock ? ` ${clock} IST` : ""}${notes}`;
      }),
      ...Object.entries(daily)
        .filter(([, row]) => row.mode === "hide")
        .map(([id, row]) => {
          const slot = dailyCat.find((s) => s.id === id);
          return `- DELETE / omit "${row.title || slot?.label || id}".`;
        })
    );
  }
  if (Object.keys(homework).length) {
    parts.push(
      "TONIGHT'S HOMEWORK ACTIVITY CRUD (homework tab — source of truth):",
      "Do not assign clock times to homework. Create / rename / delete types as listed. Homework is after the daily window.",
      ...instructionLines(homework, hwCat, false)
    );
  }
  return parts.filter(Boolean).join("\n");
}
